import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stockPicks, setStockPicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStockPicks = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Dynamic API URL detection
        const getApiUrl = () => {
          if (process.env.REACT_APP_API_URL) {
            return process.env.REACT_APP_API_URL;
          }
          
          const hostname = window.location.hostname;
          const protocol = window.location.protocol;
          
          if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'http://localhost:3000';
          } else {
            return `${protocol}//${hostname}`;
          }
        };

        const response = await fetch(`${getApiUrl()}/api/stock-picks`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const picks = await response.json();
          setStockPicks(picks);
        }
      } catch (error) {
        console.error('Error fetching stock picks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStockPicks();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-gray-600 mt-2">
            Current Plan: <span className="font-semibold capitalize">{user?.subscription?.plan || 'Free'}</span>
          </p>
        </div>

        {/* Temporarily hidden - Market Overview, Watchlist, Alerts
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="card-body">
              <h3 className="text-lg font-semibold mb-2">Market Overview</h3>
              <p className="text-gray-600">View trending stocks and market data</p>
              <div className="mt-4">
                <div className="text-2xl font-bold text-green-600">+2.4%</div>
                <div className="text-sm text-gray-500">S&P 500 Today</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3 className="text-lg font-semibold mb-2">Your Watchlist</h3>
              <p className="text-gray-600">Track your favorite stocks</p>
              <div className="mt-4">
                <div className="text-2xl font-bold text-blue-600">0</div>
                <div className="text-sm text-gray-500">Stocks tracked</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3 className="text-lg font-semibold mb-2">Alerts</h3>
              <p className="text-gray-600">Price and news alerts</p>
              <div className="mt-4">
                <div className="text-2xl font-bold text-orange-600">0</div>
                <div className="text-sm text-gray-500">Active alerts</div>
              </div>
            </div>
          </div>
        </div>
        */}

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Current AI Stock Picks</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading stock picks...</p>
            </div>
          ) : stockPicks.length > 0 ? (
            <div className="grid gap-6">
              {stockPicks.map((pick, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                          <img 
                            src={`https://financialmodelingprep.com/image-stock/${pick.symbol}.png`}
                            alt={`${pick.symbol} logo`}
                            className="w-8 h-8 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center text-white font-bold text-sm" style={{display: 'none'}}>
                            {pick.symbol.charAt(0)}
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{pick.symbol}</h3>
                          <p className="text-gray-600">{pick.company}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                          {pick.status}
                        </span>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(pick.pickDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-6 mb-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Entry Price</p>
                        <p className="text-2xl font-bold text-gray-900">${pick.pickPrice}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Target Price</p>
                        <p className="text-2xl font-bold text-blue-600">${pick.targetPrice}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Potential Gain</p>
                        <p className="text-2xl font-bold text-green-600">
                          +{Math.round(((parseFloat(pick.targetPrice) - parseFloat(pick.pickPrice)) / parseFloat(pick.pickPrice)) * 100)}%
                        </p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Analysis</h4>
                      <p className="text-gray-700 leading-relaxed">{pick.analysis}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Signals</h4>
                      <div className="flex flex-wrap gap-2">
                        {pick.signals?.map((signal, idx) => (
                          <span key={idx} className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {signal}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No stock picks available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
